import * as React from "react";
import { observer } from "mobx-react-lite";
import { StringModel, StringMobx } from "../../mobxDataModels/stringModel";
import { NameModel } from "../../mobxDataModels/nameModel";
import { notification, Modal } from "antd";
import { homeLink, linksOf, parentLink, request, resourceLink } from "../../services/cpln";
import { clearItem, randomName, updateLastDeploymentTimeOnHubspot } from "../../services/utils";
import { useNavigate } from "react-router-dom";
import { NGButton } from "../../newcomponents/button/Button";
import { NGFormElement } from "../../newcomponents/ngformelement/ngformelement";
import { Workload } from "../../schema/types/workload/workload";
import { VolumeSet } from "../../schema/types/volumeSet";
import { IpSet } from "../../schema/types/ipset";
import { WorkloadMobx } from "../../mst/kinds/workload";
import { ConsoleContext } from "../../mobxStores/consoleContext/consoleContext";
import { Gvc } from "../../schema/types/gvc";
import { RefreshCcw } from "react-feather";
import { Identity } from "../../schema/types/identity";

interface Props {
  workload: WorkloadMobx;
  onClose: () => void;
}

const WorkloadCloneModalRaw: React.FC<Props> = ({ workload, onClose }) => {
  const nameRef = React.useRef<StringMobx>(
    NameModel.create({
      validationKey: "workloadName",
    }),
  );
  const descriptionRef = React.useRef<StringMobx>(StringModel.create({ label: "Description" }));

  const secrets = React.useMemo(() => {
    const _secrets: string[] = [];
    for (const container of workload.spec?.containers || []) {
      // env vars
      for (const envVar of container.env || []) {
        if (!envVar.value.startsWith(`cpln://secret/`)) {
          continue;
        }
        const secretName = envVar.value.split(`cpln://secret/`)[1].split(".")[0];
        if (_secrets.includes(secretName)) {
          continue;
        }
        _secrets.push(secretName);
      }
      // volumes
      for (const volume of container.volumes || []) {
        if (!volume.uri.startsWith(`cpln://secret/`)) {
          continue;
        }
        const secretName = volume.uri.split(`cpln://secret/`)[1].split(".")[0];
        if (_secrets.includes(secretName)) {
          continue;
        }
        _secrets.push(secretName);
      }
    }
    return _secrets;
  }, []);

  const ipsetLink: string = React.useMemo(() => {
    if (workload.spec.loadBalancer?.direct?.ipSet) {
      return workload.spec.loadBalancer?.direct?.ipSet;
    }
    return "";
  }, []);
  const identityLink: string = React.useMemo(() => {
    return workload.spec.identityLink || "";
  }, []);

  const [isLoading, setIsLoading] = React.useState(false);

  const [isLoadingGvcs, setIsLoadingGvcs] = React.useState<boolean>(false);
  const [allGvcs, setAllGvcs] = React.useState<Gvc[]>([]);
  const [gvc, setGvc] = React.useState<string>(ConsoleContext.gvc!);

  const navigate = useNavigate();

  React.useEffect(() => {
    fetchAllGvcs();
  }, []);

  async function fetchAllGvcs() {
    try {
      setIsLoadingGvcs(true);
      let nextLink: string | undefined = `/org/${ConsoleContext.org!}/gvc`;
      let items: Gvc[] = [];
      while (nextLink) {
        const { data } = await request({ url: nextLink });
        items = items.concat(data.items);
        nextLink = linksOf(data).next;
      }
      setAllGvcs(items);
      setIsLoadingGvcs(false);
    } catch (e) {
      setIsLoadingGvcs(false);
      setAllGvcs([]);
    }
  }

  const [volumesets, setVolumesets] = React.useState<VolumeSet[]>([]);
  React.useEffect(() => {
    fetchVolumesets();
  }, [gvc]);
  async function fetchVolumesets() {
    if (workload.spec?.type !== "stateful") {
      return;
    }

    let _volumesets: VolumeSet[] = [];
    let _volumesetNames: string[] = [];
    for (const container of workload.spec?.containers || []) {
      for (const volume of container.volumes || []) {
        if (!volume.uri.startsWith("cpln://volumeset/")) {
          continue;
        }

        try {
          const volumesetName = volume.uri.split("cpln://volumeset/")[1];
          try {
            // fetch volumeset and check if it's a shared volumeset
            const { data: volumesetData } = await request<VolumeSet>({
              url: resourceLink("volumeset", volumesetName),
            });
            // Only skip shared volumeset if the gvc is the same
            if (volumesetData.spec.fileSystemType === "shared" && gvc === ConsoleContext.gvc!) {
              continue;
            }
            if (_volumesetNames.includes(volumesetData.name)) {
              continue;
            }
            _volumesetNames.push(volumesetData.name);
            _volumesets.push(volumesetData);
          } catch (e) {
            // volumeset not accessible
          }
        } catch (e) {
          // not a valid volumeset reference
        }
      }
    }
    setVolumesets(_volumesets);
  }

  const [identity, setIdentity] = React.useState<Identity>(null as any);
  React.useEffect(() => {
    fetchIdentity();
  }, []);
  async function fetchIdentity() {
    if (!identityLink) {
      return;
    }

    const { data: identityData } = await request<Identity>({
      url: identityLink,
    });
    setIdentity(identityData);
  }

  async function onClone() {
    const createdLinks: string[] = [];
    try {
      const ctx = { org: ConsoleContext.org!, gvc: gvc };
      setIsLoading(true);
      if (!nameRef.current.isValid) return;

      const { data: workloadData } = await request<Workload>({ url: workload.selfLink });
      let wl = workloadData;
      clearItem(wl);

      wl.name = nameRef.current.value;
      wl.description = descriptionRef.current.value || wl.name;

      // volumesets start
      let volumesetNameMap: { [_: string]: string } = {};
      for (const volumeset of volumesets) {
        let name = `${volumeset.name}-${wl.name}-${randomName()}`;
        if (name.length > 63) {
          name = randomName();
        }
        volumesetNameMap[volumeset.name] = name;
      }

      for (const volumeset of volumesets) {
        const { data } = await request({ url: linksOf(volumeset).self! });
        const body: VolumeSet = data;
        body.name = volumesetNameMap[volumeset.name];
        delete body.status;
        await request({ method: "post", body: body, url: parentLink("volumeset", ctx) });
        createdLinks.push(resourceLink("volumeset", body.name, ctx));
      }

      for (const container of wl.spec?.containers || []) {
        for (const volume of container.volumes || []) {
          try {
            if (!volume.uri.startsWith("cpln://volumeset/")) {
              continue;
            }
            const volumesetName = volume.uri.split("cpln://volumeset/")[1];
            const newVolumesetName = volumesetNameMap[volumesetName];
            if (!newVolumesetName) {
              continue;
            }
            volume.uri = `cpln://volumeset/${newVolumesetName}`;
          } catch (e) {}
        }
      }
      // volumesets end

      // ipset start
      if (ipsetLink) {
        const { data: ipsetData } = await request<IpSet>({ url: ipsetLink });
        let newIpsetName = `${ipsetData.name}-${wl.name}-${randomName()}`;
        if (newIpsetName.length > 63) {
          newIpsetName = randomName();
        }
        ipsetData.name = newIpsetName;
        ipsetData.spec.link = resourceLink("workload", wl.name, ctx);
        await request({ method: "post", body: ipsetData, url: homeLink("ipset") });
        const newIpsetLink = resourceLink("ipset", newIpsetName, ctx);
        createdLinks.push(newIpsetLink);

        if (wl.spec.loadBalancer?.direct) {
          wl.spec.loadBalancer.direct.ipSet = newIpsetLink;
        }
      }
      // ipset end

      // identity and policy start
      let identityBody: any = {};
      let identityName = "";
      if ((identityLink || secrets.length > 0) && gvc !== ConsoleContext.gvc!) {
        identityName = `${wl.name}-${randomName()}`;
        if (identityName.length > 63) {
          identityName = randomName();
        }
        if (identity) {
          identityBody = identity;
        } else {
          console.log("Clone workload requires identity but not found");
        }
        identityBody.name = identityName;
        identityBody.description = `Auto-managed identity for the workload "${wl.name}"`;
        wl.spec.identityLink = `//gvc/${gvc}/identity/${identityName}`;
        // Create identity
        await request({ url: parentLink("identity", ctx), body: identityBody, method: "put" });
        createdLinks.push(resourceLink("identity", identityBody.name, ctx));
      }

      if (secrets.length > 0 && gvc !== ConsoleContext.gvc!) {
        let policyName = `${wl.name}-${randomName()}`;
        if (policyName.length > 63) {
          policyName = randomName();
        }
        const policyBody = {
          name: policyName,
          kind: "policy",
          description: `Auto-created policy for the workload "${wl.name}"`,
          targetKind: "secret",
          targetLinks: secrets.map((secret) => `//secret/${secret}`),
          bindings: [{ permissions: ["reveal"], principalLinks: [`//gvc/${gvc}/identity/${identityName}`] }],
        };

        // Create policy
        await request({ url: homeLink("policy", ctx), body: policyBody, method: "put" });
        createdLinks.push(resourceLink("policy", policyBody.name, ctx));
      }
      // identity and policy end
      await request({ method: "post", body: wl, url: parentLink("workload", ctx) });

      notification.success({
        message: "Success",
        description: `Cloned Workload`,
      });
      updateLastDeploymentTimeOnHubspot();
      onClose();
      setIsLoading(false);

      if (gvc === ConsoleContext.gvc) {
        navigate(`/console${parentLink("workload")}`);
        setTimeout(() => {
          navigate(`/console${resourceLink("workload", wl.name)}`);
        }, 200);
      }
    } catch (e) {
      try {
        // cleanup created items
        const volumesetLinks = createdLinks.filter((l) => l.includes(`/volumeset/`));
        for (const link of volumesetLinks) {
          await request({ url: link, method: "delete" });
        }
        const policyLinks = createdLinks.filter((l) => l.includes(`/policy/`));
        for (const link of policyLinks) {
          await request({ url: link, method: "delete" });
        }
        const identityLinks = createdLinks.filter((l) => l.includes(`/identity/`));
        for (const link of identityLinks) {
          await request({ url: link, method: "delete" });
        }
        const ipsetLinks = createdLinks.filter((l) => l.includes(`/ipset/`));
        for (const link of ipsetLinks) {
          await request({ url: link, method: "delete" });
        }
      } catch (e) {
        notification.warning({
          duration: 5 * 60,
          message: "Failed to Clean Created Items",
          description: (
            <>
              <div className="mb-2">Created Item Links:</div>
              <div className="flex flex-col gap-1">
                {createdLinks.map((l) => (
                  <div className="text-xs">{l}</div>
                ))}
              </div>
            </>
          ),
        });
      }

      let errorMessage = e?.response?.data?.message;
      if (!errorMessage) errorMessage = e.message;
      notification.warning({
        message: "Failed",
        description: errorMessage,
      });
      setIsLoading(false);
    }
  }

  return (
    <Modal
      title="Clone Workload"
      open={true}
      maskClosable={!isLoading}
      width={650}
      onCancel={() => onClose()}
      footer={
        <div className="modal-actions">
          <NGButton variant="secondary" onClick={() => onClose()}>
            Cancel
          </NGButton>
          <NGButton variant="primary" loading={isLoading} disabled={isLoading} onClick={() => onClone()}>
            Confirm
          </NGButton>
        </div>
      }
    >
      <div className="flex flex-col" style={{ padding: "16px 8px 0" }}>
        <NGFormElement
          required
          name="name"
          label={nameRef.current.label}
          value={nameRef.current.value}
          onChange={nameRef.current.setValue}
        />
        <NGFormElement
          name="description"
          label={descriptionRef.current.label}
          value={descriptionRef.current.value}
          onChange={descriptionRef.current.setValue}
        />
        <NGFormElement
          name="gvc"
          as={"select"}
          label={"GVC"}
          value={gvc}
          info={["Select which GVC do you want to clone this workload into."]}
          onChange={(value) => setGvc(value)}
          options={allGvcs.map((gvc) => ({ label: gvc.name, value: gvc.name }))}
          innerButtons={[
            {
              title: "Refresh",
              onClick: () => {
                fetchAllGvcs();
              },
              render: () => <RefreshCcw className={`feather-icon ${isLoadingGvcs ? "animate-spin" : ""}`} />,
            },
          ]}
        />
      </div>
      <div className="flex flex-col gap-2">
        {volumesets.length > 0 ? (
          <>
            <p>
              This workload is associated with{" "}
              <b>{volumesets.length > 1 ? `${volumesets.length} volume sets` : "a single volume set"}</b>.
            </p>
            <p>Cloning the workload will also clone the volume set{volumesets.length > 1 ? "s" : ""}.</p>
            <p>
              Cloning the volume set{volumesets.length > 1 ? "s" : ""} will replicate only the specifications of the
              original volume(s); the actual data within any volume instances will <b>NOT</b> be copied.
            </p>
          </>
        ) : null}
        {ipsetLink ? (
          <p>
            This workload is associated with <b>an ip set</b>. A new ip set will be created and will be associated with
            the workload clone.
          </p>
        ) : null}
        {secrets.length > 0 && gvc !== ConsoleContext.gvc ? (
          <>
            <p>
              This workload is associated with <b>secret{secrets.length > 0 ? "s" : ""}</b>. A new{" "}
              <b>policy and identity</b> will be created and will be associated with the workload clone.
            </p>
            {identityLink ? <p>Current configuration of the identity will be replicated in the new identity.</p> : null}
          </>
        ) : null}
      </div>
    </Modal>
  );
};

export const WorkloadCloneModal = observer(WorkloadCloneModalRaw);
